import { useI18n } from "@/shared/i18n";
import { useToast } from "@/shared/ui";
import { copyToClipboard } from "@/shared/clipboard";
import type { DetectResponse } from "@/shared/api/schemas";
import "./detect.css";

/** Human-readable name per provider id the server may report. */
const PROVIDER_LABEL: Record<string, string> = {
  gptzero: "GPTZero",
  fake: "Fake detector (dev)",
};

/** Where a person can re-run the check on the vendor's own page. A score
 *  they get there is one they trust more than a number we render. */
const VERIFY_URL: Record<string, string> = {
  gptzero: "https://gptzero.me/",
};

export function providerLabel(provider: string): string {
  return PROVIDER_LABEL[provider] ?? provider;
}

export type DetectTone = "human" | "mixed" | "ai";

/** Bucket a document score for colouring; thresholds match the fake provider. */
export function toneOf(r: Pick<DetectResponse, "human_prob">): DetectTone {
  if (r.human_prob >= 0.65) return "human";
  if (r.human_prob >= 0.35) return "mixed";
  return "ai";
}

interface DetectBadgeProps {
  result: DetectResponse;
  /** The exact text that was scored — copied for the verify hand-off. */
  text: string;
}

/**
 * One-line score: provider · Human N% · date, plus "verify on the vendor's
 * site". The date is never dropped: detectors retrain, so a score is a
 * snapshot, and showing when it was taken is what keeps it honest.
 */
export function DetectBadge({ result, text }: DetectBadgeProps) {
  const { t } = useI18n();
  const toast = useToast();
  const tone = toneOf(result);
  const pct = Math.round(result.human_prob * 100);
  const date = result.checked_at.slice(0, 10);
  const label = providerLabel(result.provider);
  const verify = VERIFY_URL[result.provider];

  async function openVerify() {
    try {
      await copyToClipboard(text);
      toast.success(t("Text copied — paste it on {p} to verify", { p: label }));
    } catch {
      // Still open the page; they can copy by hand.
    }
    window.open(verify, "_blank", "noopener");
  }

  return (
    <div className={`dt-badge dt-badge--${tone}`}>
      <span className="dt-badge__dot" aria-hidden />
      <span className="dt-badge__name">{label}</span>
      <span className="dt-badge__score">{t("Human {n}%", { n: pct })}</span>
      <span className="dt-badge__date">{date}</span>
      {result.cached && <span className="dt-badge__note">{t("cached")}</span>}
      {verify && (
        <button type="button" className="dt-badge__verify" onClick={() => void openVerify()}>
          {t("Verify on {p} ↗", { p: label })}
        </button>
      )}
    </div>
  );
}
